import { Form } from "antd";
import { CounsellingRequestForm } from "./CounsellingRequestFormDialog.default";
import {
  CounsellingRequest,
  PlatformUser,
} from "../../repository/data.default";
import { useEffect, useState } from "react";
import { QueryOperation } from "../../code/search-query/search-query";
import {
  addCounsellingRequest,
  deleteCounsellingRequest,
  getPlatformUsers,
  getCollaborators,
  getCompanyCollaborators,
} from "../../repository/data.repository";
import DynamicForm from "../../controls/DynamicForm";
import InModal from "../../controls/InModal";
import { modalTitle } from "../../controls/InModal.default";
import ModalFooter from "../../controls/ModalFooter";
import ModalDeleteConfirmation from "../../controls/ModalDeleteConfirmation";
import ModalErrorAlert from "../../controls/ModalErrorAlert";
import { nameOf } from "../../utils/typescript";
import { retry, getProblemMessage } from "../../utils/http";
import {
  createScopedSearchRequest,
  createSearchRequest,
} from "../../hooks/useSearchRequest";
import dayjs from "dayjs";
import { DynamicControl } from "../../controls/DynamicForm.default";

export interface CounsellingRequestFormDialogProps {
  readonly open?: boolean;
  // Create a new request, or confirm a delete. Post-creation edits + lifecycle live in the Manage view.
  readonly mode: "add" | "delete";
  readonly record?: CounsellingRequest;
  readonly onFinish?: () => void;
  readonly onCancel?: () => void;
}

function CounsellingRequestFormDialog(
  props: CounsellingRequestFormDialogProps,
) {
  const [form] = Form.useForm();
  const values = Form.useWatch<CounsellingRequestForm>([], form);

  const [valid, setValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  // Company UIDs the selected therapist is assigned to — used to scope the patient picker (therapist → patient).
  const [therapistCompanies, setTherapistCompanies] = useState<string[]>([]);

  useEffect(() => {
    if (!props.open) {
      reset();
    }
  }, [props.open]);

  // Resolve the chosen therapist's assigned companies (via the CompanyCollaborators join) so the patient
  // picker can be limited to users in those companies. Mirrors the backend rule that a therapist must be
  // assigned to the request's company (the reverse direction is scoped directly off the patient's company).
  useEffect(() => {
    const therapistUid = values?.therapist?.value;
    if (!therapistUid) {
      setTherapistCompanies([]);
      return;
    }
    let cancelled = false;
    getCompanyCollaborators({
      searchQuery: {
        conditions: [
          { field: "userUid", operation: QueryOperation.Equal, values: [therapistUid] },
        ],
      },
      pageNumber: 0,
      pageSize: 100,
    })
      .then(({ data }) => {
        if (!cancelled) setTherapistCompanies(data.map((r) => r.companyUid));
      })
      .catch(() => {
        if (!cancelled) setTherapistCompanies([]);
      });
    return () => {
      cancelled = true;
    };
  }, [values?.therapist?.value]);

  useEffect(() => {
    form
      .validateFields({
        validateOnly: true,
      })
      .then((value) => {
        setValid(!!Object.values(value).length);
      })
      .catch(() => {
        setValid(false);
      });
  }, [form, values, props.open]);

  function onSubmit(): void {
    setLoading(true);
    setErrorMessage("");
    if (props.mode === "add") {
      const { therapist, patient, reason, proposedAt } = values;
      addCounsellingRequest({
        therapistUid: therapist.value,
        patientUid: patient?.value,
        reason,
        proposedAt: proposedAt ? dayjs(proposedAt).toDate() : undefined,
      })
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch((err) => {
          setLoading(false);
          setErrorMessage(getProblemMessage(err));
        });
    } else if (props.mode === "delete") {
      deleteCounsellingRequest(props.record?.uid!)
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch((err) => {
          setLoading(false);
          setErrorMessage(getProblemMessage(err));
        });
    }
  }

  function onCancel(): void {
    props.onCancel?.();
  }

  // Methods

  function reset(): void {
    setLoading(false);
    setValid(false);
    setErrorMessage("");
    form.resetFields();
  }

  const platformUsersRequest = (search: string) => {
    const searchQuery = {
      conditions: [
        {
          field: nameOf<PlatformUser>("userName"),
          operation: QueryOperation.StartsWith,
          values: [search],
        },
      ],
    };
    return retry(() =>
      getPlatformUsers({
        searchQuery,
        pageNumber: 0,
        pageSize: 100,
      }),
    ).then(({ data }) =>
      data
        // therapist → patient: when a therapist is chosen, only show users in that therapist's companies.
        .filter(
          (u) =>
            !therapistCompanies.length ||
            (u.companyUid && therapistCompanies.includes(u.companyUid)),
        )
        .map((dataItem) => ({
          display: dataItem.userName,
          subDisplay: dataItem.companyName,
          value: dataItem.uid,
          dataItem,
        })),
    );
  };

  // patient → therapist: when a patient is chosen, only show therapists assigned to the patient's company.
  const companyCollaboratorsRequest = createScopedSearchRequest(
    getCompanyCollaborators,
    "userUserName",
    "companyUid",
    () => (values?.patient as any)?.dataItem?.companyUid,
    undefined,
    { valueField: "userUid", subDisplayField: "companyName" },
  );
  const allCollaboratorsRequest = createSearchRequest(
    getCollaborators,
    "userName",
    undefined,
    { subDisplayField: "companyName" },
  );
  const collaboratorsRequest = (search: string) =>
    (values?.patient as any)?.dataItem?.companyUid
      ? companyCollaboratorsRequest(search)
      : allCollaboratorsRequest(search);

  return (
    <InModal
      open={props.open}
      size={props.mode === "delete" ? "small" : "medium"}
      title={modalTitle(props.mode, "Request", undefined)}
      onCancel={onCancel}
      actions={
        <ModalFooter
          mode={props.mode}
          loading={loading}
          valid={valid}
          onCancel={onCancel}
          onSubmit={onSubmit}
        />
      }
    >
      {props.mode !== "delete" && (
        <DynamicForm
          className="w-auto"
          form={form}
          schema={{
            groups: [
              {
                controls: [
                  {
                    type: "combobox",
                    name: "therapist",
                    label: "Therapist",
                    request: collaboratorsRequest,
                    rules: [
                      {
                        required: true,
                        message: "Therapist is required",
                      },
                    ],
                  },
                  {
                    type: "combobox",
                    name: "patient",
                    label: "Patient (subject)",
                    request: platformUsersRequest,
                  },
                  {
                    type: "areabox",
                    name: "reason",
                    label: "Reason",
                  },
                  // Only on create: an optional first slot opens the request directly in Proposed.
                  ...((props.mode === "add"
                    ? [
                        {
                          type: "datetimepicker",
                          label: "Proposed slot (optional)",
                          name: "proposedAt",
                          format: (value: any) =>
                            dayjs(value).format("DD/MM/YYYY HH:mm"),
                        },
                      ]
                    : []) as DynamicControl[]),
                ],
              },
            ],
          }}
        />
      )}
      {props.mode === "delete" && (
        <ModalDeleteConfirmation entityLabel="request" entityName="Counselling" />
      )}
      <ModalErrorAlert message={errorMessage} />
    </InModal>
  );
}

export default CounsellingRequestFormDialog;
